
import { useEffect, useState } from 'react';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import { Modal, ModalBody, ModalContent, ModalTrigger } from '@/common-utils/components/ui/modal';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { cn } from '@/common-utils/libraries/tailwind/utils';
import actionsPosTerminals from '../../../api/actionsPosTerminals';
import { DateTime } from 'luxon';
import useCheckConnectionPoints from '../../../hooks/useCheckConnectionPoints';
import DateTimePicker from '@/common-utils/components/renders/dateTimePicker/DateTimePicker';
import PropTypes from 'prop-types';
import { useToast } from '@/common-utils/hooks/use-toast';
import { nsTerminal } from '@/app/i18n/namespaces';

/**
 * @param {JSX.Element} trigger element that triggers modal opening
 * @returns {JSX.Element} ModalBlacklistPosTerminal Component
 */
function ModalScheduledBlacklistPosTerminal({ trigger, isOpen, setIsOpen, subscriptionId, setLaunchRefetch }) {

    // Toast
    const { toast } = useToast();

    // State
    const [bodyContent, setBodyContent] = useState(<></>);
    const [showCalendar, setShowCalendar] = useState(false);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [dateTimeValues, setDateTimeValues] = useState(undefined);

    // API Calls
    // Queries
    const getConnectionPointsResponse = actionsPosTerminals.getConnectionPoints(subscriptionId);

    // Mutations
    const onSuccess = () => {
        setLaunchRefetch(true);
        setIsOpen(false);
        toast({
            description:
                <Entity
                    entity={'modal.blacklist.scheduled.success'}
                    namespace={nsTerminal}
                    data={{
                        date: DateTime.fromISO(dateTimeValues.startDateSelected).toLocaleString(DateTime.DATE_SHORT),
                        time: DateTime.fromISO(dateTimeValues.startDateSelected).toLocaleString(DateTime.TIME_SIMPLE)
                    }}
                />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity={'modal.blacklist.scheduled.error'} namespace={nsTerminal} />,
            variant: 'danger'
        });
    };
    const submitBlacklistPosTerminal = actionsPosTerminals.blacklistPosTerminal(onSuccess, onError);

    // Hooks
    useCheckConnectionPoints(getConnectionPointsResponse, setBodyContent, 'modal.blacklist.scheduled');

    useEffect(() => {
        if (getConnectionPointsResponse.data && !getConnectionPointsResponse.isLoading) {
            setShowCalendar(true);
        }
    }, [getConnectionPointsResponse.data, getConnectionPointsResponse.isLoading]);

    useEffect(() => {
        if (dateTimeValues && dateTimeValues.startDateSelected) {
            setIsSubmitDisabled(false);
        } else {
            setIsSubmitDisabled(true);
        }
    }, [dateTimeValues]);

    // Renders
    const content =
        <ModalContent className={cn('max-w-lg')}>
            <HeaderTitle title={'modal.blacklist.title'} namespace={nsTerminal} />
            <ModalBody>
                <div className={showCalendar ? 'flex flex-col items-center' : ''}>
                    {
                        <div className={showCalendar ? 'text-lg' : ''}>
                            {bodyContent}
                        </div>
                    }
                    {
                        showCalendar &&
                        <>
                            <DateTimePicker
                                setDateTimeValues={setDateTimeValues}
                                rangeDisabled={(date) => date <= new Date()}
                            />
                            {
                                dateTimeValues && dateTimeValues.startDateSelected &&
                                <Entity
                                    entity={'modal.blacklist.scheduled.summary'}
                                    data={{
                                        date: DateTime.fromISO(dateTimeValues.startDateSelected).toLocaleString(DateTime.DATE_SHORT),
                                        time: DateTime.fromISO(dateTimeValues.startDateSelected).toLocaleString(DateTime.TIME_SIMPLE)
                                    }}
                                    namespace={nsTerminal}
                                    className='text-base mt-4'
                                />

                            }
                        </>
                    }
                </div>
            </ModalBody>
            <FooterForm
                onSubmit={() => submitBlacklistPosTerminal.mutate({
                    subscriptionId,
                    blacklistStartDate: dateTimeValues.startDateSelected
                })}
                isSubmitting={submitBlacklistPosTerminal.isLoading}
                isSubmitDisabled={isSubmitDisabled}
                showSubmitButton={getConnectionPointsResponse.data}
            />
        </ModalContent>;

    if (trigger) {
        return (
            <Modal>
                <ModalTrigger>{trigger}</ModalTrigger>
                {content}
            </Modal>
        );
    }

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            {content}
        </Modal>
    );
}

ModalScheduledBlacklistPosTerminal.propTypes = {
    trigger: PropTypes.element,
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    subscriptionId: PropTypes.string,
    setLaunchRefetch: PropTypes.func
};

export default ModalScheduledBlacklistPosTerminal;